/**
 * The words of the question asked about hiring when someone is deployed to a store (product
 * owner, 21 Oct 2026: "when we are redeploying any other employee to that store where hiring is
 * going on ... it does not ask whether we need to cancel the hiring, stop the hiring process, or
 * move that hiring to another store"; 22 Oct 2026: asked BEFORE the deploy is saved), and the
 * small rules that pick them, so a test can hold them without drawing a page. The window itself
 * is DeployHiringCheck.
 *
 * Short on purpose: a heading, a line, and choices of two to four words; what a choice does is
 * on its hover. Move, Cancel and Stop following keep the opening window's own words, so the
 * choice made here reads the same as the window that then finishes it.
 */
import type { HiringLink, HiringRequest } from '../../services/api';
import { STAFFING_STATUS } from '../../constants/storeStatus';
import { CANCEL_WORDS, MOVE_WORDS, OPENING_WORDS, RAISE_WORDS, REQUEST_WORDS, openingTitle } from './hiringText';

const counted = (n: number, one: string): string => `${n} ${n === 1 ? one : `${one}s`}`;

export const DEPLOY_CHECK_WORDS = {
  /** The window's title after the deploy is saved, and before it when two stores (a swap) are asked about. */
  title: 'Check hiring',
  /* ── before the deploy is saved: the store someone joins already has hiring ── */
  openHeading: (store: string): string => `Hiring is open at ${store}`,
  /** Only a hiring request waits there: nothing has been raised yet. */
  askedHeading: (store: string): string => `Hiring request waiting at ${store}`,
  /**
   * Under the heading: the deploy being made is why the question is asked (product owner,
   * 22 Oct 2026: "was just deployed here" read as "was already deployed before"). A two-way
   * swap says it for each store, with the person that store gets. The question is put only when
   * there is something to decide (`asks`): a person who may only see hiring gets the first
   * sentence alone. Only a request waiting: it is the request that is asked about.
   */
  joinedLine: (person: string, asks: 'hiring' | 'request' | null = 'hiring'): string =>
    (asks ? `You’re deploying ${person} here. What should happen to this ${asks}?` : `You’re deploying ${person} here.`),
  openHiring: 'Open hiring',
  openHiringHover: (store: string): string => `Open hiring for ${store}`,
  keep: 'Keep hiring',
  keepHover: {
    short: 'The store still needs people. Its hiring stays as it is.',
    plain: 'Its hiring stays as it is.',
  },
  /** "Keep" when only a hiring request waits at the store: nothing is hiring there yet. */
  keepRequest: 'Keep request',
  keepRequestHover: {
    short: 'The store still needs people. The request stays as it is.',
    plain: 'The request stays as it is.',
  },
  move: MOVE_WORDS.button,
  moveHover: MOVE_WORDS.buttonExplained,
  cancel: CANCEL_WORDS.button,
  cancelHover: CANCEL_WORDS.cancelExplained,
  stop: OPENING_WORDS.stop,
  stopHover: CANCEL_WORDS.stopExplained,
  withdraw: 'Withdraw',
  withdrawHover: REQUEST_WORDS.withdraw,
  decline: REQUEST_WORDS.decline,
  declineHover: 'Decline this hiring request, with a reason the person who asked will read.',
  /** The window was shown, and then everything there was closed from "Open hiring". */
  nothingOpenNow: 'No hiring is open there now.',
  /** The choices of one opening, or of the waiting request, for a screen reader. */
  choicesOf: (line: string): string => `What happens to ${line}`,
  requestChoices: 'What happens to the hiring request',
  /* ── the window's foot, before anything is saved ── */
  dontDeploy: 'Don’t deploy',
  dontDeployHover: 'Nothing is saved. You go back to the deploy as it was.',
  deploy: 'Deploy',
  deployHover: 'Save the deploy, then do what you chose here.',
  /** "Deploy" where the person has nothing to choose (Hiring (view) alone, or nothing open now). */
  deployHoverPlain: 'Save the deploy.',
  deploying: 'Deploying…',
  /* ── after the deploy is saved ── */
  /**
   * At the top of the window that finishes a Move, a Cancel, a Withdraw or a Decline chosen
   * before the deploy: the deploy is saved, and stands whatever happens in that window.
   */
  finishLine: (person: string, step: 'move' | 'cancel' | 'withdraw' | 'decline', what: string): string => {
    const doing = { move: 'moving', cancel: 'cancelling', withdraw: 'withdrawing', decline: 'declining' }[step];
    return `${person} is deployed. Now finish ${doing} ${what}.`;
  },
  /** What a finish line says of a waiting request. */
  theRequest: 'the hiring request',
  /** "Stop following", chosen before the deploy, is done at once after it: what became of each. */
  doneHeading: 'Done',
  stopping: (opening: string): string => `Stopping following ${opening}…`,
  stopped: (opening: string): string => `Stopped following ${opening}`,
  stopFailed: (opening: string): string => `Could not stop following ${opening}.`,
  stopFailedNext: 'Open the store’s hiring to try again.',
  /* ── part 2: the store the person left ── */
  needsHeading: (store: string): string => `${store} needs someone`,
  /** Whether the store is short cannot be told from what is known: the question is put as an "if". */
  mayNeedHeading: (store: string): string => `If ${store} needs someone`,
  movedLine: (person: string): string => `${person} moved out. No hiring is open there.`,
  /** The heading names the store; the button stays short, with the owner's "Raise hiring for <store>" on its hover. */
  raise: RAISE_WORDS.panelButton,
  raiseHover: (store: string): string => `${RAISE_WORDS.rowControl(store)}. Nothing is sent before Review.`,
  ask: REQUEST_WORDS.panelButton,
  askHover: (store: string): string => `${REQUEST_WORDS.rowControl(store)}. Nothing is sent before Review.`,
  notNow: 'Not now',
  notNowHover: 'Nothing is sent. You can do this from the store later.',
  close: 'Close',
} as const;

/** An opening as a sentence names it: "Opening 48213", or its title when it has no number yet. */
export const deployOpeningRef = (link: HiringLink): string =>
  (link.openingId != null ? `Opening ${link.openingId}` : openingTitle(link));

/**
 * One opening on one line: "Sample Promoter · Opening 48213 · 2 open · 5 candidates". The
 * candidates are those still in process; a figure that was not read is left out, and a request
 * still being raised says so in place of figures.
 */
export const deployOpeningLine = (link: HiringLink): string => {
  const parts = [openingTitle(link)];
  if (link.state === 'sending') return [...parts, 'being raised'].join(' · ');
  if (link.numbers) parts.push(`${link.numbers.open} open`);
  if (link.stages) parts.push(counted(link.stages.inProcess, 'candidate'));
  return parts.join(' · ');
};

/** A hiring request waiting at the store, on one line: its budget, and whose it is. */
export const deployRequestLine = (request: HiringRequest): string => {
  const budget = String(request.budget?.name ?? '').trim();
  const what = request.mine ? 'Your hiring request is waiting' : 'Hiring request waiting';
  return budget ? `${budget} · ${what}` : what;
};

/** Whether the store a person left needs someone: yes, perhaps (it cannot be told), or no. */
export type LeftStoreNeed = 'short' | 'maybe' | 'no';

/**
 * The store a person just moved out of, as GET /stores/:id answers it after the move: does it
 * need someone? What is cheaply known, in this order:
 *   - the person is still deployed there (a roaming role keeps its stores): they did not leave it;
 *   - "Hiring not needed": a person said it needs nobody hired (no deploy ever overwrites it);
 *   - "Hiring to be done", or nobody left at it: it is short;
 *   - "Deployed": its minimum is met (the server turns it to "Hiring to be done" below it);
 *   - any other staffing status with people still there: the category's minimum is not in the
 *     answer, so it cannot be told, and the question is put as an "if".
 */
export const leftStoreNeedOf = (
  store: { staffing_status?: unknown; employee_count?: unknown; employees?: unknown },
  personCode?: string | null,
): LeftStoreNeed => {
  const people = Array.isArray(store.employees) ? (store.employees as Array<{ employee_code?: unknown }>) : null;
  const code = String(personCode ?? '').trim().toLowerCase();
  if (code && people?.some((p) => String(p?.employee_code ?? '').trim().toLowerCase() === code)) return 'no';
  const status = String(store.staffing_status ?? '').trim();
  if (status === STAFFING_STATUS.HIRING_NOT_NEEDED) return 'no';
  if (status === STAFFING_STATUS.HIRING_TO_BE_DONE) return 'short';
  const count = Number(store.employee_count ?? people?.length);
  if (Number.isFinite(count) && count <= 0) return 'short';
  if (status === STAFFING_STATUS.DEPLOYED) return 'no';
  return 'maybe';
};
